import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import FolioRule from "@/components/FolioRule";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSubmissions() {
  const { token } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/submissions`, { headers })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (_e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-submissions">
      <FolioRule label="01 / SUBMISSIONS" className="mb-12" />
      <h1 className="font-display text-5xl md:text-6xl leading-tight text-cream">{t("admin.submissions")}</h1>

      <div className="mt-14 border border-gold/20">
        {loading ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/45">
            {t("admin.no_submissions")}
          </div>
        ) : (
          rows.map((s, i) => (
            <div key={s.id} className={`${i > 0 ? "border-t border-gold/15" : ""}`} data-testid={`submission-${s.id}`}>
              <button
                onClick={() => setOpen(open === s.id ? null : s.id)}
                className="w-full grid md:grid-cols-12 gap-4 px-6 py-5 text-left hover:bg-gold/5 transition-colors duration-500"
              >
                <div className="md:col-span-3 font-display italic text-cream text-lg">{s.name}</div>
                <div className="md:col-span-3 text-cream/70 text-sm">{s.email}</div>
                <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-gold/80">
                  {s.path === "raw_material" ? "Raw Material" : "Private Label"}
                </div>
                <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
                  {new Date(s.created_at).toLocaleString()}
                </div>
              </button>
              {open === s.id ? (
                <div className="px-6 pb-8 grid md:grid-cols-12 gap-6 border-t border-gold/15 pt-6">
                  <div className="md:col-span-9 space-y-3 text-cream/85 font-light text-sm">
                    {s.company ? (
                      <Row k="Company" v={s.company} />
                    ) : null}
                    {s.country ? <Row k="Country" v={s.country} /> : null}
                    <Row k="Master blender" v={s.has_master_blender === null ? "—" : s.has_master_blender ? "Yes" : "No"} />
                    <Row k="Custom development" v={s.needs_custom_dev === null ? "—" : s.needs_custom_dev ? "Yes" : "No"} />
                    <Row k="New brand" v={s.new_brand === null ? "—" : s.new_brand ? "Yes" : "No"} />
                    <Row k="Message" v={s.message} />
                  </div>
                  <div className="md:col-span-3 flex md:justify-end">
                    <button
                      onClick={() => onDelete(s.id)}
                      className="btn-luxe text-destructive border-destructive/40 hover:bg-destructive/10"
                      data-testid={`delete-${s.id}`}
                    >
                      {t("admin.delete")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gold/10">
      <div className="font-mono text-[10px] tracking-widest2 uppercase text-cream/45">{k}</div>
      <div className="col-span-2 whitespace-pre-line">{v}</div>
    </div>
  );
}
